import { logger } from 'shared/infrastructure/logger';

const RENDER_WRAPPER_CLASS_DEFAULT = 'mint-lesson';

// Rsbuild copia aquí los assets de MathJax v3 (ver `output.copy` en rsbuild.config.ts). Ruta
// raíz absoluta (no toAbsoluteUrl/PUBLIC_URL): los assets copiados se sirven desde la raíz en
// todos los clientes, también Edelvives (que solo prefija /edelvives/ a los assets del HTML).
const MATHJAX_PUBLIC_PATH = '/mathjax';
// El id es necesario para que el loader de v3 resuelva su ruta base (fuentes, input jaxes)
// relativa a MATHJAX_PUBLIC_PATH.
const MATHJAX_SCRIPT_ID = 'MathJax-script';
// Plazo máximo de arranque. Sin él un fallo de despliegue deja `ready` pendiente para siempre:
// nginx (`try_files $uri $uri/ /index.html`) responde index.html con 200 OK cuando /mathjax/ no
// existe, y un <script> que recibe HTML dispara `load`, no `error`.
const MATHJAX_STARTUP_TIMEOUT_MS = 15000;

// Ciclo de vida del motor v3, a nivel de módulo para que sea único en toda la app.
let mathJaxGlobal = null;
let bundleLoad = null;
let hasFailed = false;
let markReady;
let failReady;
const ready = new Promise((resolve, reject) => {
  markReady = resolve;
  failReady = reject;
});
// Guardia: si `ready` rechaza sin que nadie haya encolado nada (vistas que renderizan con
// KaTeX y nunca tipografían), el rechazo saldría como `unhandledrejection` global.
ready.catch(() => {});
// El plazo de arranque rechaza esta promesa y no `ready`: si v3 arranca tarde, lo que se encole
// a partir de entonces tipografía con normalidad.
let failStartupDeadline;
const startupDeadline = new Promise((resolve, reject) => {
  failStartupDeadline = reject;
});
startupDeadline.catch(() => {});

let hasReportedKatexLoadFailure = false;
let hasReportedKatexRenderFailure = false;

function createScriptTags({ id, src }) {
  return new Promise(function (resolve, reject) {
    const s = document.createElement('script');
    let r = false;
    s.type = 'text/javascript';
    s.id = id;
    s.src = src;
    // s.async = true;
    s.onerror = function (err) {
      reject(err, s);
    };
    s.onload = s.onreadystatechange = function () {
      // console.log(this.readyState); // uncomment this line to see which ready states are called.
      if (!r && (!this.readyState || this.readyState === 'complete')) {
        // console.log('se ha cargado Mathjax correctamente', window.MathJax);
        r = true;
        resolve();
      }
    };
    const t = document.getElementsByTagName('script')[0];
    t.parentElement.insertBefore(s, t);
  });
}

function toNodeArray(elements) {
  if (elements == null) return undefined;
  if (elements.nodeType) return [elements];
  // `Hub.Typeset` de v2 aceptaba un id en string; sin este caso `slice.call('id')` lo trocearía
  // en ['i', 'd'].
  if (typeof elements === 'string') {
    const node = document.getElementById(elements);
    return node ? [node] : undefined;
  }
  return Array.prototype.slice.call(elements);
}

// Ejecuta un spec de `Hub.Queue` de v2, que aceptaba tanto una función como la forma
// [method, context, ...args] (con `method` como clave del contexto o como función suelta).
function runSpec(spec) {
  if (typeof spec === 'function') return spec();
  if (Array.isArray(spec)) {
    const [method, context, ...rest] = spec;
    const fn = typeof method === 'function' ? method : context[method];
    return fn.apply(context, rest);
  }
  return undefined;
}

function reportError(message, error) {
  console.error(message, error);
  logger.error(message, { error, tags: { engine: 'mathjax' } });
}

// Mantiene viva la cadena y evita `unhandledrejection`: ni las libs (su try/catch no captura
// rechazos async) ni renderMathJax encadenan un catch propio.
function onTypesetError(error) {
  reportError('MathJax: error tipografiando fórmulas', error);
}

// Sin saltos de línea automáticos en v3, una fórmula en bloque ancha se desplaza dentro de su
// container (`_mathjax.scss`). Safari y Chrome < 130 no hacen enfocable un elemento con scroll,
// así que sin `tabindex` ese desplazamiento no se alcanza con teclado (WCAG 2.1.1).
function makeOverflowingFormulasFocusable(nodes) {
  (nodes ?? [document]).forEach((root) => {
    root.querySelectorAll?.("mjx-container[display='true']").forEach((container) => {
      if (container.scrollWidth > container.clientWidth) container.setAttribute('tabindex', '0');
    });
  });
}

export function buildMathJaxHubShim(whenReady) {
  // Shim de la API v2 `MathJax.Hub` que v3 no tiene. Mint/Lemonade la llaman en cuanto ven
  // window.MathJax (que fijamos síncronamente), posiblemente ANTES de que v3 haya cargado.
  // `whenReady()` dispara la carga perezosa del bundle y resuelve cuando v3 termina su startup:
  // encolamos sobre ella y traducimos a typesetPromise, así su try/catch nunca se traga nada.

  // Cadena única de peticiones. `Hub.Queue` de v2 era una cola global serializada, y
  // `typesetPromise` de v3 NO es reentrante: `options.elements` es estado compartido y
  // `render()` reintenta al cargar fuentes CHTML y extensiones autoload, así que dos llamadas
  // concurrentes se pisan (salida parcial o excepción). Encadenar preserva la garantía de v2.
  let typesetChain = Promise.resolve();

  function typeset(elements, callback) {
    const nodes = toNodeArray(elements);
    typesetChain = typesetChain.then(() => whenReady()).then(
      () =>
        Promise.resolve(globalThis.MathJax?.typesetPromise?.(nodes))
          .then(() => makeOverflowingFormulasFocusable(nodes))
          .catch(onTypesetError),
      // Si el motor no arranca, el fallo ya lo registró quien rechazó `whenReady()`; aquí solo
      // se descarta para no repetirlo por cada typeset encolado.
      () => {}
    );
    return typeof callback === 'function' ? typesetChain.then(callback) : typesetChain;
  }

  return {
    Typeset: typeset,
    // Las libs lo llaman en la config inline que inyectan al cargar su propio v2.
    Config: function () {},
    Queue: function () {
      // Los specs corren en orden; los que tipografían delegan en `typeset`, que ya espera a
      // `whenReady()` y llama al callback del spec aunque el motor no arranque.
      return Array.prototype.slice
        .call(arguments)
        .reduce((chain, spec) => chain.then(() => runSpec(spec)), Promise.resolve())
        .catch(onTypesetError);
    },
  };
}

function buildMathJaxConfig() {
  return {
    // Sin `colorv2`: v2 cargaba `color.js`, con el que `\color` actúa como interruptor (colorea
    // lo que le sigue hasta el final del grupo). `colorv2` lo sustituye por la forma de v2 sin
    // extensión (solo el siguiente átomo).
    // `failed`: si una extensión no carga, v3 llama aquí y deja `startup.promise` pendiente.
    loader: { load: ['input/asciimath', '[tex]/color', '[tex]/cancel'], failed: handleBundleFailure },
    options: { ignoreHtmlClass: 'no-mathjax|editor-page', processHtmlClass: 'fr-math', enableMenu: false },
    tex: {
      packages: { '[+]': ['color', 'cancel'] },
      inlineMath: [[String.raw`\(`, String.raw`\)`]],
      autoload: { color: [] },
    },
    chtml: { displayAlign: 'left' },
    startup: {
      typeset: false,
      ready: () => {
        try {
          globalThis.MathJax.startup.defaultReady();
          ignoreAssistiveMathML();
        } finally {
          markReady();
        }
      },
    },
  };
}

/** Evita que cada re-typeset anide otro `mjx-container` alrededor del MathML asistivo de v3. */
export function ignoreAssistiveMathML() {
  // El buscador de MathML no atiende a `ignoreHtmlClass` y reencuentra el asistivo que v3 deja
  // dentro del container; los editores Mint/Lemonade re-tipografían en cada `html.set`. Solo se
  // filtra ese nodo: el MathML del contenido se sigue procesando.
  const mathML = globalThis.MathJax?.startup?.document?.inputJax?.find((jax) => jax.name === 'MathML');
  if (!mathML) return;

  const findMath = mathML.findMath.bind(mathML);
  mathML.findMath = (node) => findMath(node).filter((item) => !item.start?.node?.closest?.('mjx-assistive-mml'));
}

export function claimMathJaxGlobal() {
  // Reclamamos el global de forma síncrona SIN cargar todavía el bundle (~1,1 MB): basta con que
  // el addMathJax() propio de Mint/Lemonade lo vea ocupado para que no cargue su CDN 2.7.7. La
  // descarga real se dispara de forma perezosa en el primer typeset encolado (ver
  // `loadMathJaxBundle`): la mayoría de vistas renderizan con KaTeX y solo las de Mint (visor y
  // edición, en app y Edelvives) piden MathJax.
  if (mathJaxGlobal || globalThis.MathJax || globalThis.loadingMathLib) return;
  globalThis.loadingMathLib = true;

  // v3 no respeta el objeto que encuentra en el global: si no tiene `version` lo sustituye por
  // uno propio y mueve el nuestro a `MathJax.config`. Hub desaparecería durante toda la carga, y
  // las libs hacen `window.MathJax && window.MathJax.Hub.Queue(...)` sin proteger `.Hub`
  // (TypeError). Interceptamos la asignación para reinyectarlo en el objeto nuevo.
  const v2Compat = { Hub: buildMathJaxHubShim(loadMathJaxBundle) };
  mathJaxGlobal = { ...buildMathJaxConfig(), ...v2Compat };
  Object.defineProperty(globalThis, 'MathJax', {
    configurable: true,
    get: () => mathJaxGlobal,
    set: (value) => {
      mathJaxGlobal = Object.assign(value, v2Compat);
    },
  });
}

function handleBundleFailure(error) {
  // Una sola vez: v3 llama a `loader.failed` por cada extensión que no carga, y su rechazo vuelve
  // aquí por la carrera de `injectBundle`.
  if (hasFailed) return;
  hasFailed = true;
  reportError(`MathJax: fallo cargando el motor v3 desde ${MATHJAX_PUBLIC_PATH}`, error);
  // No borramos el global: `delete window.MathJax` + `loadingMathLib = false` reproduce la
  // condición que Mint/Lemonade comprueban (`!window.MathJax && !window.loadingMathLib`) y les
  // haría cargar su CDN 2.7.7 en paralelo, con dos motores peleando por el global. Rechazamos
  // `ready` para que lo encolado falle rápido en vez de quedar colgado.
  failReady(error);
}

function startStartupTimeout() {
  const timeoutId = setTimeout(() => {
    const error = new Error(`MathJax v3 no completó su arranque en ${MATHJAX_STARTUP_TIMEOUT_MS} ms`);
    reportError(`MathJax: ${error.message}`, error);
    failStartupDeadline(error);
  }, MATHJAX_STARTUP_TIMEOUT_MS);
  const clearStartupTimeout = () => clearTimeout(timeoutId);
  ready.then(clearStartupTimeout, clearStartupTimeout);
}

function injectBundle() {
  return (
    createScriptTags({ id: MATHJAX_SCRIPT_ID, src: `${MATHJAX_PUBLIC_PATH}/tex-mml-chtml.js` })
      .then(() => {
        // El plazo empieza cuando el bundle ya se ha ejecutado: si contara su descarga (~1,1 MB),
        // una red lenta lo agotaría sin que nada hubiera fallado.
        startStartupTimeout();
        // `startup.promise` rechaza si falla `pageReady`, pero no si falla una extensión: v3
        // llama a `loader.failed` y la deja pendiente. Por eso compite con `ready`.
        return Promise.race([globalThis.MathJax?.startup?.promise, ready]);
      })
      .catch(handleBundleFailure)
  );
}

function loadMathJaxBundle() {
  claimMathJaxGlobal();
  // Otro MathJax (o una carga ajena en curso) ocupa el global: no inyectamos el nuestro.
  if (!mathJaxGlobal) return Promise.resolve();

  // Idempotente: el <script> del bundle se inyecta una sola vez, también tras un fallo.
  if (!bundleLoad) bundleLoad = injectBundle();
  // `ready` va primero: si v3 arranca después de vencer el plazo, gana aunque los dos estén ya
  // resueltos, y lo encolado a partir de entonces tipografía.
  return bundleLoad.then(() => Promise.race([ready, startupDeadline]));
}

export function addMathJax() {
  return loadMathJaxBundle();
}

// Del paquete instalado y no del CDN (DEU-321): donde el CDN está bloqueado las fórmulas salían sin estilo.
// Se deja en globalThis porque los editores y el visor de @oneclick llaman a `renderMathInElement` y `katex` globales.
// `katex` se asigna en cuanto llega, sin esperar a auto-render: esas libs piden KaTeX al CDN si no ven `window.katex`.
function addKatex() {
  return Promise.all([
    import('katex').then((katex) => {
      globalThis.katex = katex.default;
    }),
    import('katex/contrib/auto-render').then((autoRender) => {
      globalThis.renderMathInElement = autoRender.default;
    }),
    import('katex/dist/katex.min.css'),
  ]);
}

// El ChunkLoadError de Rsbuild trae en `request` la URL del chunk que no llegó.
function failedResource(error) {
  const url = error?.request;
  return typeof url === 'string' ? url.split(/[?#]/)[0] : undefined;
}

// Sin `error` a propósito: el filtro `chunk-load-error` de Sentry (docs/sentry/filtros.md) descarta las excepciones
// ChunkLoadError, y este aviso existe para ver en Sentry qué recurso falla. Por lo mismo es `warn` y no `info`.
function reportKatexLoadFailure(error) {
  if (hasReportedKatexLoadFailure) return;
  hasReportedKatexLoadFailure = true;
  logger.warn('KaTeX: fallo cargando el motor', { tags: { engine: 'katex' }, extra: { resource: failedResource(error) } });
}

// Un fallo al pintar es un bug de quien llama, como pasar el objeto ref en vez del elemento (CoursePage de Edelvives).
function reportKatexRenderFailure(error) {
  if (hasReportedKatexRenderFailure) return;
  hasReportedKatexRenderFailure = true;
  logger.error('KaTeX: fallo pintando las fórmulas', { error, tags: { engine: 'katex' } });
}

function firstRender(targetElement = null) {
  // Si se proporciona un elemento específico, lo usamos
  // si no, buscamos el elemento por clase
  const elements = document.getElementsByClassName(RENDER_WRAPPER_CLASS_DEFAULT);
  const renderElement = targetElement || (elements.length > 0 ? elements[0] : null);

  // Validamos que lemonadeContainer exista antes de continuar
  if (!renderElement) {
    console.warn('No se encontró ningún elemento para renderizar las fórmulas matemáticas');
    return;
  }

  // Aplicamos el renderizado según el motor matemático disponible
  if (window.renderMathInElement) {
    window.renderMathInElement(renderElement);
  } else if (globalThis.MathJax?.Hub?.Typeset) {
    // Vía Hub.Typeset, que encola: `typesetPromise` directo descartaría el render en silencio
    // si el bundle de v3 todavía no ha terminado su startup.
    globalThis.MathJax.Hub.Typeset([renderElement]);
  }
}

/**
 * Carga KaTeX (librería + estilos + auto-render) y resuelve cuando está disponible.
 *
 * Existe para que quien necesite `window.renderMathInElement` pueda garantizarlo en vez de
 * asumirlo. Hasta TAN-10415 nadie lo cargaba explícitamente en la mayoría de rutas: llegaba de
 * rebote porque lemonade pedía KaTeX para sus actividades y el script quedaba en la página. Al
 * pasar lemonade a MathJax dejó de llegar, y `TextHtml`/`TextRich` —111 sitios: comentarios,
 * listas de ítems, diálogos de vista previa— se quedaron mostrando el LaTeX en crudo.
 *
 * Sin caché propia desde DEU-321: KaTeX sale del paquete con `import()`, que el runtime de Rsbuild
 * ya resuelve una sola vez aunque se monten muchos `TextHtml` a la vez. El rechazo se propaga para
 * que `renderKatex` lo recoja.
 */
export function loadKatex() {
  if (globalThis.renderMathInElement) return Promise.resolve();

  return addKatex().catch((error) => {
    reportKatexLoadFailure(error);
    throw error;
  });
}

export function addLatex(mathEngine, targetElement = null) {
  if (mathEngine === 'katex') {
    return addKatex()
      .then(() => firstRender(targetElement), reportKatexLoadFailure)
      .catch(reportKatexRenderFailure);
  }
  return (
    addMathJax()
      .then(() => firstRender(targetElement))
      // El fallo ya se registró en `handleBundleFailure`; sin catch saldría como
      // `unhandledrejection`.
      .catch(() => {})
  );
}
